import React from 'react';
import { useNode, BuilderComponent, DesignMode } from '@mnz/builder';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import Link from '../../common/components/link/DesignLink';
import StyledComponent from '../../core/StyledComponent';
import PlusIcon from '@menuzen/icon/build/outline/Plus';
import { SocialSetting, LinksSettingProps } from './SocialSetting';
import LabelIcon from '../../common/components/label-icon/LabelIcon';
import { socialOptions } from '../../constant/socialLinks';
import { capitalizeFirstLetter } from '../../shared/utils/string';
import { _order } from '../../shared/utils';

export const Social: BuilderComponent<LinksSettingProps> = ({
    designSettings,
    account,
    hideSocialLinkItems,
    order,
}) => {
    const {
        connectors: { connect, drag },
        query: { getOptions },
        hidden,
    } = useNode((node) => ({
        hidden: node.data.hidden,
    }));
    const canEdit = getOptions().enabled;
    const links = _order(account.socialLinks, order || []);

    return (
        <StyledComponent
            xs={{
                marginTop: '15px',
                marginBottom: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {!hidden && (
                <StyledComponent ref={(ref) => connect(drag(ref))}>
                    <Stack spacing={1} direction="row">
                        {[0, 1, 2, 3, 4, 5].map((index) => {
                            const accountSocial = links[index];
                            const socialOption = accountSocial
                                ? socialOptions.find((item) => item.value == accountSocial.type)
                                : null;
                            if (socialOption && !hideSocialLinkItems?.[accountSocial.id]) {
                                const link =
                                    socialOption && socialOption.handleLink
                                        ? socialOption.handleLink(accountSocial.link)
                                        : accountSocial?.link;
                                return (
                                    <Link
                                        type={accountSocial.type.toLowerCase()}
                                        url={link}
                                        isLiveMode={getOptions().mode === DesignMode.LIVE}
                                        key={index}
                                    >
                                        <LabelIcon
                                            type={designSettings.styles.icon}
                                            icon={capitalizeFirstLetter(accountSocial.type.toLowerCase())}
                                            size={36}
                                            color={designSettings.colors.text.color}
                                            corner={designSettings.styles.corner}
                                            backgroundColor={designSettings.colors?.socialButton?.color}
                                        />
                                    </Link>
                                );
                            }

                            if (canEdit) {
                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            width: 35,
                                            height: 35,
                                            fontSize: 16,
                                            display: 'flex',
                                            alignItems: 'center',
                                            boxSizing: 'border-box',
                                            justifyContent: 'center',
                                            color: '#ABAEB2',
                                            backgroundColor: '#F8F8F9',
                                            cursor: 'pointer',
                                            border: '1px solid #EFEFF0',
                                            borderRadius: '4px',
                                            transitionProperty: 'color, border-color !important',
                                            transition: '0.5s',
                                            ':hover': {
                                                borderColor: (theme) => theme.palette.success.main,
                                                borderWidth: '2px',
                                                color: (theme) => theme.palette.success.main,
                                            },
                                        }}
                                    >
                                        <PlusIcon />
                                    </Box>
                                );
                            }
                        })}
                    </Stack>
                </StyledComponent>
            )}
        </StyledComponent>
    );
};

Social.defaultProps = {
    hideSocialLinkItems: {},
};

Social.settings = {
    displayName: 'Social Links',
    related: {
        settings: SocialSetting,
    },
    rules: {
        canDrag: () => false,
    },
    custom: {
        showBox: false,
    },
};
